/**
 * RevokeAccessButton — Destructive action button.
 *
 * Active: "Revoke Access" / Pending: "Cancel Invitation"
 * Shows Alert confirmation dialog before calling onPress.
 */

import React, { useCallback } from 'react';
import { View, Text, Pressable, Alert, StyleSheet } from 'react-native';
import { useTranslation } from '@dramadetector/shared';

interface RevokeAccessButtonProps {
  status: 'active' | 'pending';
  onPress: () => void;
  requireConfirmation?: boolean;
}

export const RevokeAccessButton: React.FC<RevokeAccessButtonProps> = React.memo(
  function RevokeAccessButton({
    status,
    onPress,
    requireConfirmation = true,
  }) {
    const { t } = useTranslation('practitioner_consent');
    const { t: tCommon } = useTranslation('common');

    const label =
      status === 'active'
        ? t('revoke.active_label')
        : t('revoke.pending_label');

    const icon = status === 'active' ? '✕' : '⊘';

    const handlePress = useCallback(() => {
      if (!requireConfirmation) {
        onPress();
        return;
      }

      Alert.alert(
        t('revoke.confirm_title'),
        t('revoke.confirm_body'),
        [
          { text: tCommon('cancel'), style: 'cancel' },
          { text: t('revoke.confirm_cta'), style: 'destructive', onPress },
        ]
      );
    }, [onPress, requireConfirmation, t, tCommon]);

    return (
      <View style={styles.container}>
        <Pressable
          onPress={handlePress}
          style={({ pressed }) => [
            styles.button,
            pressed && styles.buttonPressed,
          ]}
          accessibilityRole="button"
          accessibilityLabel={label}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Text style={styles.icon} accessibilityElementsHidden={true}>
            {icon}
          </Text>
          <Text style={styles.label}>{label}</Text>
        </Pressable>
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 0.5,
    borderTopColor: 'rgba(255, 255, 255, 0.4)',
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  buttonPressed: {
    opacity: 0.7,
  },
  icon: {
    fontSize: 14,
    color: '#EF4444',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#EF4444',
  },
});
